import { Grid } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalPalette, RoutalSelect, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import More3Points from '../../../assets/svg/More3Points';
import { HeaderContent } from '../../../components/HeaderContent';
import { ContextualMenu } from '../../../components/menus/ContextualMenu';
import { OrganizationUser } from '../../../domain/organization/OrganizationUser';
import { useDeleteOrganizationUser } from '../../../hooks/organization/useDeleteOrganizationUser';
import useResendOrganizationUserInvitation from '../../../hooks/organization/useResendOrganizationUserInvitation';
import useRestoreOrganizationUser from '../../../hooks/organization/useRestoreOrganizationUser';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationUsers } from '../../../hooks/organization/useRetrieveOrganizationUsers';
import { useUpdateOrganizationUser } from '../../../hooks/organization/useUpdateOrganizationUser';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../services/requests';
import { OrganizationNewUserDialog } from './OrganizationNewUserDialog';
const sortByName = (users: OrganizationUser[]) => {
  return users.sort((a: OrganizationUser, b: OrganizationUser) => {
    const fullnameA = `${a.first_name} ${a.last_name}`;
    const fullnameB = `${b.first_name} ${b.last_name}`;
    // Compare the 2 fullnames
    if (fullnameA < fullnameB) return -1;
    if (fullnameA > fullnameB) return 1;
    return 0;
  });
};

/**
 * Order users by fullname, pending and deleted.
 */
const sortUsers = (users: OrganizationUser[]): OrganizationUser[] => {
  try {
    const activeUsers = sortByName(users.filter((user) => !user.deleted && user.validated));
    const nonValidatedUsers = sortByName(users.filter((user) => !user.deleted && !user.validated));
    const deletedUsers = sortByName(users.filter((user) => user.deleted));
    return activeUsers.concat(nonValidatedUsers).concat(deletedUsers);
  } catch (_) {
    return [];
  }
};

export const OrganizationUsers = () => {
  const { data: organization } = useRetrieveOrganization();
  const { data: loggedUser } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: organizationUsers } = useRetrieveOrganizationUsers();
  const { run: removeOrganizationUser } = useDeleteOrganizationUser({ organization: organization! });
  const { run: editOrganizationUser } = useUpdateOrganizationUser({ organization: organization! });
  const { run: resendUserInvitation } = useResendOrganizationUserInvitation({
    organization: organization!,
  });
  const { run: restoreOrganizationUser } = useRestoreOrganizationUser({
    organization: organization!,
  });
  const [resendInvitationLoading, setResendInvitationLoading] = useState<string>(``);
  const [newUserDialogOpen, setNewUserDialogOpen] = useState<boolean>(false);
  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; user: OrganizationUser } | undefined
  >(undefined);

  if (!organizationUsers) return null;

  const sortedOrganizationUsers = sortUsers(organizationUsers);

  return (
    <Grid container direction="column" style={{ backgroundColor: RoutalPalette.neutral02n }}>
      <Grid container direction="column">
        <Grid
          container
          direction="column"
          style={{
            padding: `11px 20px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
          wrap="nowrap"
        >
          <Grid container direction="row" wrap="nowrap">
            <HeaderContent
              titleIntlId="settings.organization.users.title"
              titleIntlValues={{ organizationName: organization?.label }}
              styles={{
                container: {
                  display: `flex`,
                  flexDirection: `column`,
                  justifyContent: `center`,
                },
              }}
            />

            {userCredentials?.subscription.test(`planner.users`) && (
              <Grid container direction="row" wrap="nowrap" justifyContent="flex-end">
                <Button
                  styleType="primary"
                  variant="default"
                  onClick={() => setNewUserDialogOpen(true)}
                  disabled={!userCredentials?.test(`can_create_and_delete_users`)}
                  intlId="settings.organization.users.buttons.create"
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
        wrap="nowrap"
      >
        <DataTable
          noFooter
          docs={sortedOrganizationUsers.map((user) => ({ doc: user }))}
          total={sortedOrganizationUsers.length}
          showCheckboxes={false}
          handleOnContextMenu={(ev: any, user: OrganizationUser) => {
            ev.preventDefault();
            if (userCredentials?.test(`can_create_and_delete_users`)) {
              setTableContextualAnchor({ screenPosition: { top: ev.clientY, left: ev.clientX }, user });
            }
          }}
          columns={[
            {
              width: `calc(100% - 450px)`,
              accessorName: `name`,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="settings.organization.users.name" />
              ),
              formatter: ({ doc: user }: { doc: OrganizationUser }) => (
                <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                  <Grid item>
                    <RoutalAvatar
                      url={user?.avatar_url}
                      getUploadAsBase64={getUploadAsBase64}
                      label={`${user.first_name}_${user.last_name}`}
                      labelStyles={{ fontSize: `12px`, lineHeight: 2 }}
                      containerStyles={{ width: `32px`, height: `32px` }}
                    />
                  </Grid>

                  <HSpacer medium />

                  <Grid container direction="column" justifyContent="center">
                    <Grid item>
                      <Text
                        variant="body-large"
                        style={{
                          ...(user.deleted ? { textDecoration: `line-through` } : {}),
                          ...(!user.validated ? { color: `#76727B` } : {}),
                        }}
                      >
                        {`${user.first_name} ${user.last_name}`}
                        {user.validated && organization?.isOwner(user.id) && (
                          <Text variant="body" tagName="span" intlId="settings.organization.users.owner" />
                        )}
                        {!user.validated && !user.deleted && (
                          <Text variant="body" tagName="span" intlId="settings.organization.users.pending" />
                        )}
                        {user.validated &&
                          !organization?.isOwner(user.id) &&
                          loggedUser &&
                          user.id === loggedUser.id && (
                            <Text variant="body" tagName="span" intlId="settings.organization.users.myself" />
                          )}
                      </Text>
                    </Grid>

                    <VSpacer small />

                    <Grid item>
                      <Text
                        variant="body-large"
                        style={{
                          ...(!user.validated ? { color: `#76727B` } : {}),
                        }}
                      >
                        {user.email}
                      </Text>
                    </Grid>
                  </Grid>
                </Grid>
              ),
            },
            {
              accessorName: `role`,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20, textTransform: `none` }}
                  isBold
                  intlId="settings.organization.users.organization_role"
                />
              ),
              formatter: ({ doc: organizationUser }: { doc: OrganizationUser }) => {
                return (
                  <RoutalSelect
                    id="organization-user-roles"
                    containerStyle={{
                      width: `250px`,
                    }}
                    disabled={
                      organizationUser.deleted ||
                      organization?.isOwner(organizationUser.id) ||
                      (loggedUser && organizationUser.id === loggedUser.id) ||
                      !userCredentials?.test(`can_create_and_delete_users`)
                    }
                    popoverProps={{
                      maxHeight: `400px`,
                      options: [
                        {
                          key: `organization_administrator`,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            color: `#929599`,
                            height: `100%`,
                            padding: `8px 16px`,
                            borderBottom: `1px solid #E0E0E0`,
                            ...(organizationUser.role === `organization_administrator`
                              ? { backgroundColor: `#f8671d14` }
                              : {}),
                          },
                          content: (
                            <Grid container direction="column" wrap="nowrap">
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body-medium"
                                intlId="settings.organization.users.roles.administrator"
                              />
                              <VSpacer small />
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body"
                                intlId="settings.organization.users.roles.administrator.description"
                              />
                            </Grid>
                          ),
                          onClick: () => editOrganizationUser({ organizationUser, role: `organization_administrator` }),
                        },
                        {
                          key: `organization_manager`,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            color: `#929599`,
                            height: `100%`,
                            padding: `8px 16px`,
                            borderBottom: `1px solid #E0E0E0`,
                            ...(organizationUser.role === `organization_manager`
                              ? { backgroundColor: `#f8671d14` }
                              : {}),
                          },
                          content: (
                            <Grid container direction="column" wrap="nowrap">
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body-medium"
                                intlId="settings.organization.users.roles.manager"
                              />
                              <VSpacer small />
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body"
                                intlId="settings.organization.users.roles.manager.description"
                              />
                            </Grid>
                          ),
                          onClick: () => editOrganizationUser({ organizationUser, role: `organization_manager` }),
                        },
                        {
                          key: `organization_user`,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            color: `#929599`,
                            height: `100%`,
                            padding: `8px 16px`,
                            ...(organizationUser.role === `organization_user` ? { backgroundColor: `#f8671d14` } : {}),
                          },
                          content: (
                            <Grid container direction="column" wrap="nowrap">
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body-medium"
                                intlId="settings.organization.users.roles.user"
                              />
                              <VSpacer small />
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body"
                                intlId="settings.organization.users.roles.user.description"
                              />
                            </Grid>
                          ),
                          onClick: () => editOrganizationUser({ organizationUser, role: `organization_user` }),
                        },
                      ],
                    }}
                  >
                    <Text
                      variant="body"
                      style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                      intlId={`settings.organization.users.roles.${
                        organizationUser.role === `organization_administrator`
                          ? `administrator`
                          : organizationUser.role === `organization_manager`
                          ? `manager`
                          : `user`
                      }`}
                    />
                  </RoutalSelect>
                );
              },
            },
            userCredentials?.test(`can_create_and_delete_users`)
              ? {
                  accessorName: `actions`,
                  align: `right`,
                  formatter: ({ doc: organizationUser }: { doc: OrganizationUser }) => {
                    if (
                      organization?.isOwner(organizationUser.id) ||
                      (loggedUser && organizationUser.id === loggedUser.id)
                    ) {
                      return null;
                    }

                    return (
                      <RoutalSelect
                        id="organization-user-actions"
                        hideArrow
                        style={{
                          width: `28px`,
                          height: `28px`,
                          padding: `2px`,
                          margin: `0px`,
                          border: `none`,
                          backgroundColor: `transparent`,
                          color: `#393939`,
                        }}
                        popoverProps={{
                          anchor: {
                            anchorOrigin: {
                              vertical: `bottom`,
                              horizontal: `right`,
                            },
                            transformOrigin: {
                              vertical: `top`,
                              horizontal: `right`,
                            },
                          },
                          options: [
                            ...(!organizationUser.validated && !organizationUser.deleted
                              ? [
                                  {
                                    key: `resend`,
                                    disabled: resendInvitationLoading === organizationUser.id,
                                    onClick: () => {
                                      setResendInvitationLoading(organizationUser.id);
                                      resendUserInvitation({ userId: organizationUser.id });
                                      setTimeout(() => setResendInvitationLoading(``), 1500);
                                    },
                                    content: (
                                      <Text
                                        intlId="settings.organization.users.buttons.resend"
                                        style={{ cursor: `pointer` }}
                                      />
                                    ),
                                  },
                                ]
                              : []),
                            ...(organizationUser.deleted
                              ? [
                                  {
                                    key: `restore`,
                                    onClick: () => {
                                      restoreOrganizationUser({ userId: organizationUser.id });
                                    },
                                    content: (
                                      <Text
                                        intlId="settings.organization.users.buttons.restore"
                                        style={{ cursor: `pointer` }}
                                      />
                                    ),
                                  },
                                ]
                              : [
                                  ...(!organization?.isOwner(organizationUser.id) &&
                                  !(loggedUser && organizationUser.id === loggedUser.id)
                                    ? [
                                        ...(!organizationUser.validated && !organizationUser.deleted ? [null] : []),
                                        {
                                          key: `delete`,
                                          onClick: () => {
                                            removeOrganizationUser({ organizationUser });
                                          },
                                          content: (
                                            <Text
                                              intlId="settings.organization.users.buttons.delete"
                                              style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                                            />
                                          ),
                                        },
                                      ]
                                    : []),
                                ]),
                          ],
                        }}
                      >
                        <More3Points />
                      </RoutalSelect>
                    );
                  },
                }
              : {},
          ]}
        />

        {tableContextualAnchor ? (
          <ContextualMenu
            anchorElementPosition={tableContextualAnchor.screenPosition}
            onClose={() => {
              setTableContextualAnchor(undefined);
            }}
            options={
              userCredentials?.test(`can_create_and_delete_users`)
                ? [
                    ...(!tableContextualAnchor.user.validated && !tableContextualAnchor.user.deleted
                      ? [
                          {
                            key: `resend`,
                            disabled: resendInvitationLoading === tableContextualAnchor.user.id,
                            text: (
                              <Text intlId="settings.organization.users.buttons.resend" style={{ cursor: `pointer` }} />
                            ),
                            onClick: () => {
                              setResendInvitationLoading(tableContextualAnchor.user.id);
                              resendUserInvitation({ userId: tableContextualAnchor.user.id });
                              setTimeout(() => setResendInvitationLoading(``), 1500);
                            },
                          },
                        ]
                      : []),
                    ...(tableContextualAnchor.user.deleted
                      ? [
                          {
                            key: `restore`,
                            disabled: resendInvitationLoading === tableContextualAnchor.user.id,
                            text: (
                              <Text
                                intlId="settings.organization.users.buttons.restore"
                                style={{ cursor: `pointer` }}
                              />
                            ),
                            onClick: () => restoreOrganizationUser({ userId: tableContextualAnchor.user.id }),
                          },
                        ]
                      : !organization?.isOwner(tableContextualAnchor.user.id) &&
                        !(loggedUser && tableContextualAnchor.user.id === loggedUser.id)
                      ? [
                          {
                            key: `delete`,
                            disabled: resendInvitationLoading === tableContextualAnchor.user.id,
                            text: (
                              <Text
                                intlId="settings.organization.users.buttons.delete"
                                style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                              />
                            ),
                            onClick: () => {
                              removeOrganizationUser({ organizationUser: tableContextualAnchor.user });
                            },
                          },
                        ]
                      : []),
                  ]
                : []
            }
          />
        ) : null}
      </Grid>

      <OrganizationNewUserDialog
        isOpen={newUserDialogOpen}
        refreshProjects
        onClose={() => setNewUserDialogOpen(false)}
      />
    </Grid>
  );
};
